import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { Playlists, type PlaylistInfoOut } from "../lib/api";
import { useCatalog } from "../contexts/CatalogContext";

function parseExpiry(v: PlaylistInfoOut["exp_date"]): Date | null {
  if (v === null || v === undefined || v === "") return null;
  // Xtream returns a unix timestamp as string ("1750000000") or number.
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Date(n * 1000);
}

function daysUntil(d: Date): number {
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

function formatExpiry(d: Date): string {
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PlaylistInfoBadge() {
  const { activePlaylist } = useCatalog();
  const [info, setInfo] = useState<PlaylistInfoOut | null>(null);

  useEffect(() => {
    setInfo(null);
    if (!activePlaylist) return;
    if (activePlaylist.kind !== "xtream") return;
    let cancelled = false;
    Playlists.info(activePlaylist.id)
      .then((d) => { if (!cancelled) setInfo(d); })
      .catch(() => { if (!cancelled) setInfo(null); });
    return () => { cancelled = true; };
  }, [activePlaylist]);

  if (!activePlaylist || activePlaylist.kind !== "xtream" || !info) return null;
  const exp = parseExpiry(info.exp_date);
  // Nothing useful to show. Don't render an empty card.
  if (!exp && !info.status) return null;

  const days = exp ? daysUntil(exp) : null;
  // Colour the date when the line is close to expiry or already expired.
  const dateClass = (() => {
    if (days === null) return "text-text-primary";
    if (days <= 0) return "text-danger";
    if (days <= 7) return "text-accent-gold";
    return "text-text-primary";
  })();

  return (
    <div className="flex items-center gap-2.5 px-3 py-2 bg-surface/60 rounded-xl border border-muted/30">
      <span className="w-7 h-7 rounded-lg bg-accent-gold/15 text-accent-gold flex items-center justify-center flex-shrink-0">
        <Clock size={13} />
      </span>
      <div className="min-w-0 flex-1">
        {exp ? (
          <p className={`text-[12px] font-ui font-medium truncate ${dateClass}`}>
            {days !== null && days <= 0 ? "Expired" : "Expires"} {formatExpiry(exp)}
          </p>
        ) : (
          <p className="text-[12px] font-ui font-medium text-text-primary truncate">
            {info.status || "—"}
          </p>
        )}
        <p className="text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary truncate">
          {info.status && exp ? info.status : null}
          {info.status && exp && info.max_connections ? " · " : null}
          {info.max_connections
            ? `${info.active_cons ?? 0}/${info.max_connections} conns`
            : null}
        </p>
      </div>
    </div>
  );
}
